import type { StatusResponse } from '../api/types.js';
import { formatGallons } from '../util/format.js';
import { BatteryIndicator, LOW_BATTERY_THRESHOLD } from './BatteryIndicator.js';
import styles from './StatusFooter.module.css';

interface StatusFooterProps {
  status: StatusResponse | undefined;
}

function relativeTime(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function isLowBatteryAlert(status: StatusResponse): boolean {
  // Only alert when actually running on battery; PowerLine devices should not
  // get a "replace battery" nag.
  return (
    status.lastBattery !== undefined &&
    status.lastBattery <= LOW_BATTERY_THRESHOLD &&
    status.lastPowerSupply === 'battery'
  );
}

export function StatusFooter({ status }: StatusFooterProps) {
  if (status === undefined) {
    return <footer className={styles.footer}>Loading status…</footer>;
  }

  const showStaleAlert = status.isStale || status.lastError !== undefined;
  const showLowBatteryAlert = isLowBatteryAlert(status);

  return (
    <footer className={styles.footer}>
      {showStaleAlert && (
        <div className={styles.alert} role="alert">
          {status.lastError !== undefined ? (
            <>
              <strong>Ingest error:</strong> {status.lastError}
              {status.lastErrorAt !== undefined ? ` (${relativeTime(status.lastErrorAt)})` : null}
            </>
          ) : (
            <>
              <strong>No recent data.</strong> Last successful ingest was{' '}
              {status.lastIngestAt !== undefined
                ? relativeTime(status.lastIngestAt)
                : 'never recorded'}
              .
            </>
          )}
        </div>
      )}

      {showLowBatteryAlert && (
        <div className={styles.alert} role="alert">
          <strong>Battery is low.</strong> Replace the FlowSmart battery on your next visit.
        </div>
      )}

      <div className={styles.row}>
        <span>
          Last ingest:{' '}
          <strong>
            {status.lastIngestAt !== undefined ? relativeTime(status.lastIngestAt) : 'pending'}
          </strong>
        </span>
        <span>
          Cumulative:{' '}
          <strong>
            {status.lastCumulativeGallons !== undefined
              ? formatGallons(status.lastCumulativeGallons)
              : '—'}
          </strong>
        </span>
        {status.lastBattery !== undefined && (
          <span className={styles.batteryCell}>
            Battery: <BatteryIndicator level={status.lastBattery} />
          </span>
        )}
      </div>
    </footer>
  );
}
